import React from 'react';
import * as d3 from 'd3';
import Transition from 'join-transition';

import Nodes from './Nodes';

import './style.css';

const Axis = ({ scale, gridHeight, format, numTicks }) => {
  const tickFormat = scale.tickFormat(numTicks || 6, format || '.1s');
  return (
    <g className="axis">
      {scale.ticks(numTicks || 6).map(tick => (
        <g key={tick} transform={`translate(${scale(tick)},0)`}>
          <line y2={6} />
          <line y2={-gridHeight} className="grid" />
          <text y={5} textAnchor="middle" dy="1em">
            {tickFormat(tick)}
          </text>
        </g>
      ))}
      <line x1={scale.range()[0]} x2={scale.range()[1]} strokeWidth={1} />
    </g>
  );
};

const GroupNodes = ({ nodes, color }) => 
  <g>
    {nodes.map((node, i) => { 
      return node.value && 
        (<g
          className={`node node-id-${node.key}`}
          key={node.key + i}
          transform={`translate(${node.x0},${node.y0})`}
          style={{ opacity: node.opacity }} >
          <rect
            width={node.x1 - node.x0}
            height={node.y1 - node.y0}
            fill={color} />
        </g>)
    })}
  </g>

export default class Graphic extends React.Component {
  constructor(props) {
    super(props);
    this.labelRenderableNode = this.labelRenderableNode.bind(this);
    this.setHoverNode = this.setHoverNode.bind(this);
  }

  state = { renderAggregates: true, hoverNode: null };
  componentWillReceiveProps(nextProps) {
    this.setState({
      renderAggregates: this.props.aggregateBy === nextProps.aggregateBy,
    });
  }

  setHoverNode(hoverNode){
    this.setState({ hoverNode })
  }

  interpolate(a, b, interpolate) {
    const { trailingTransitionKeys } = this.props;

    if (trailingTransitionKeys) {
      const intermediate = (a, b) => {
        const m = { ...b };
        trailingTransitionKeys.forEach(key => {
          m[key] = a[key];
        });
        m.children =
          m.children &&
          m.children.map((child, i) => intermediate(a.children[i], child));
        return m;
      };

      const m = intermediate(a, b),
        i1 = interpolate(a, m),
        i2 = interpolate(m, b);
      return t => ({
        ...(t < 0.5
          ? i1(d3.easeCubicOut(t * 2))
          : i2(d3.easeCubicIn(t * 2 - 1))),
        key: b.key,
        data: b.data,
      });
    }

    return interpolate(a, b);
  }

  transitionProps = {
    identify: d => `${d.side}-${d.key}`,
    duration: 1000,
    stagger: 3 / 8,
    interpolate: this.interpolate.bind(this),
  };

  labelRenderableNode(node) {
    const { layout, width } = this.props;
    const tooSmallY = 30;
    const tooSmallX = Math.max(33, 6+8*node.key.length);
    const mobile = width < 729;

    const treemapLayout = layout === 'Treemap';
    const barchartLayout = layout === 'Bar chart';

    const textWrap = node.key.search(' ') !== -1 &&
      ((treemapLayout &&
        node.x1 - node.x0 < 10 * node.key.length &&
        node.y1 - node.y0 >= tooSmallY) ||
        (barchartLayout &&
          10 * node.key.length > this.props.margin.left));

    const textNoBreak = +(node.y1 - node.y0 < tooSmallY);

    let textYOffset, labelYOffset, labelXOffset, showLabel;
    if(treemapLayout){
      textYOffset = 16;
      labelYOffset = 12;
      labelXOffset = 5;
      showLabel = ((node.y1 - node.y0) > tooSmallY && (node.x1 - node.x0) > tooSmallX);
    } else if (barchartLayout){
      textYOffset = textWrap 
        ? (node.y1 - node.y0)/2 - (mobile ? 0 : 2)
        : (node.y1 - node.y0)/2 + (mobile ? 2 : 4);
      labelYOffset = textWrap 
        ? (mobile ? -5 : -7)
        : 0;
      labelXOffset = 5;
      showLabel = mobile || node.y1 - node.y0 > 16;
    }

    return {
      ...node,
      key: node.key.replace(/\s/g, "-"),
      textNoBreak,
      textYOffset,
      labelYOffset,
      labelXOffset,
      showLabel,
      textWrap,
      treemapLayout,

      secondLineOffset: mobile ? 8 : 12,
      opacity: 1,
      percentageX: barchartLayout ? node.x1 - node.x0 : 0,
      percentageInsideClass: treemapLayout ||
        (barchartLayout && !(node.x1 - node.x0 < 33)),
      percentageInsidePosition: barchartLayout &&
        node.x1 - node.x0 < 33
        ? mobile 
          ? 30
          : 40
        : 0,
    };
  }

  render() {
    const {
      nodes,
      groupNodes,
      layout,
      aggregateBy,
      colorScale,
      size,
      sideSize,
      margin,
      valueScale,
      afterOffset,
      numTicks,
      benchmarks,
    } = this.props;

    const formatPercentage = d3.format('.1%');

    const axis = (
      <Axis
        scale={valueScale}
        gridHeight={sideSize[1] - (margin.top || 0) - (margin.bottom || 0)}
        format={d3.format('.0%')}
        numTicks={numTicks}
      />
    );
    const benchmarkStrokes = benchmarks.before.map(b => b[0].name).reduce((t,v,i) => ({
      ...t,
      [v]: i === 0 ? '' : `${4/i} ${4/i}`
    }), {})

    return (
      <div className="graphic">
        <svg width={size[0]} height={size[1]}>

          {layout === 'Bar chart'
            ? <g>
                <g transform={`translate(${margin.left || 0}, ${sideSize[1] - (margin.bottom || 0)})`}>
                  {axis}
                </g>
                <g transform={`translate(${afterOffset[0] + (margin.left || 0)}, 
                    ${afterOffset[1] + sideSize[1] - (margin.bottom || 0)})`}>
                  {axis}
                </g>
              </g>
            : null}

          {layout === 'Bar chart' && benchmarks
            // exposures benchmark are vertical line segments on each row with right-aligned hover value
            ? <g className="benchmarks">
                {["before","after"].map((side,i) => 
                  <g key={`${side}-${i}`} 
                    transform={`translate(${(i*afterOffset[0]) + (margin.left || 0)}, ${(i*afterOffset[1]) + (margin.top || 0)})`}>
                    {benchmarks[side].map((b,bi) => {
                      const hoveredBenchmark = this.state.hoverNode && b.find(e => 
                        e.value === this.state.hoverNode || e.value === this.state.hoverNode.replace('-',' '))
                      return (<g key={`${side}-${i}-${bi}`}>
                        <text className="benchmark-name"
                          x={sideSize[0] - margin.left - 0.75*margin.right} 
                          y={b[0].y0 - 5} >
                          {b[0].name}
                        </text>
                        {b.map((e,ei) => 
                          <line 
                            key={`${side}-${i}-${bi}-${ei}`} 
                            strokeDasharray={benchmarkStrokes[e.name]} 
                            x1={e.x} 
                            x2={e.x} 
                            y1={e.y0} 
                            y2={e.y1} /> )}
                        {hoveredBenchmark
                          ? <text className="benchmark-hovered"
                              x={sideSize[0] - margin.left - 0.75*margin.right}
                              y={(hoveredBenchmark.y0 + hoveredBenchmark.y1)/2 + 3}>
                              {formatPercentage(hoveredBenchmark.weighting)}
                            </text>
                          : null }
                      </g>)
                    })}
                </g>)}
              </g>
            : null}

          {/* Group nodes */}
          <Transition
            values={groupNodes.map(this.labelRenderableNode)}
            enterOrExit={d => ({ ...d, opacity: 0 })}
            interpolate={(a, b, interpolate) => t => ({
              ...interpolate(a, b),
              data: b.data,
              key: b.key,
            })}
            {...this.transitionProps}
            duration={groupNodes.length ? 3*this.transitionProps.duration : 0}
            stagger={groupNodes.length ? .33 : 0}
          >
            {groupNodes => {
              return (
                <g className={"group-nodes "+layout.replace(" ","-")}>
                  <GroupNodes
                    nodes={groupNodes.filter(node => node.side === 'before')}
                    color={colorScale('before')}
                  />
                  <GroupNodes
                    nodes={groupNodes.filter(node => node.side === 'after')}
                    color={colorScale('after')}
                  />
                </g>
              );
            }}
          </Transition>

          {/* Value nodes */}
          <Transition
            values={nodes.map(this.labelRenderableNode)}
            enterOrExit={d => ({ ...d, opacity: 0 })}
            interpolate={(a, b, interpolate) => t => ({
              ...interpolate(a, b),
              data: b.data,
              key: b.key,
            })}
            {...this.transitionProps}
          >
            {nodes => {
              const childNodeByKey = {};
              nodes.forEach(({ side, children }) => {
                children.forEach(child => {
                  childNodeByKey[`${side}-${child.key}`] = child;
                });
              });

              return (
                <g className={"nodes "+layout.replace(" ","-")}>
                  <Nodes
                    nodes={nodes.filter(node => node.side === 'before')}
                    aggregateBy={aggregateBy}
                    layout={layout}
                    align={layout === 'Bar chart' ? 'right' : 'left'}
                    formatPercentage={formatPercentage}
                    color={colorScale('before')}
                    setHoverNode={this.setHoverNode}
                    hoverNode={this.state.hoverNode}
                  />
                  <Nodes
                    nodes={nodes.filter(node => node.side === 'after')}
                    aggregateBy={aggregateBy}
                    layout={layout}
                    align={layout === 'Treemap' ? 'left' : 'right'}
                    formatPercentage={formatPercentage}
                    color={colorScale('after')}
                    setHoverNode={this.setHoverNode}
                    hoverNode={this.state.hoverNode}
                  />
                </g>
              );
            }}
          </Transition>

        </svg>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Graphic/Component.js